import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import SEOHead from '@/components/marketplace/SEOHead';
import { nodeUrl } from '@/lib/taxonomyUrl';
import { Cog, Wrench, Layers, Zap, Truck, Package, Factory, ArrowRight, ChevronDown } from 'lucide-react';

const catIcon = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('machin')) return Cog;
  if (n.includes('tool') || n.includes('spare')) return Wrench;
  if (n.includes('raw')) return Layers;
  if (n.includes('electric') || n.includes('electron')) return Zap;
  if (n.includes('material handling') || n.includes('utility') || n.includes('heavy')) return Truck;
  if (n.includes('equipment')) return Factory;
  return Package;
};

const SectionHeader = ({ listingType, count }) => {
  const label = (
    listingType === 'sell' ? 'Sell'
    : listingType === 'wanted' ? 'Wanted'
    : listingType === 'rent' ? 'Rent'
    : 'Services'
  );
  const linkPath = listingType === 'service' ? '/services' : `/${listingType}`;
  return (
    <div className='flex items-end justify-between mb-4 mt-8'>
      <div>
        <h2 className='text-2xl font-semibold tracking-tight'>Categories under {label}</h2>
        <p className='text-muted-foreground text-sm'>{count} main categories · click to browse</p>
      </div>
      <Link to={linkPath} className='hidden sm:inline'>
        <Button variant='outline' size='sm' data-testid={`categories-view-all-${listingType}`}>
          View all {label.toLowerCase()} listings <ArrowRight className='h-4 w-4 ml-1.5' />
        </Button>
      </Link>
    </div>
  );
};

const INITIAL_SUB_COUNT = 10;

const CategoryCard = ({ main, listingType }) => {
  const Icon = catIcon(main.name);
  const [expanded, setExpanded] = useState(false);
  const total = main.subcategories?.length || 0;
  const shownSubs = expanded ? main.subcategories : main.subcategories.slice(0, INITIAL_SUB_COUNT);
  const hiddenCount = Math.max(0, total - INITIAL_SUB_COUNT);

  return (
    <Card className='p-5' data-testid={`categories-main-${listingType}-${main.slug}`}>
      <Link
        to={nodeUrl(main)}
        className='flex items-start gap-3 group'
      >
        <div className='h-10 w-10 rounded-lg bg-secondary flex items-center justify-center shrink-0 group-hover:bg-background'>
          <Icon className='h-5 w-5 text-primary' />
        </div>
        <div className='flex-1 min-w-0'>
          <div className='font-semibold group-hover:underline underline-offset-4'>{main.name}</div>
          <div className='text-xs text-muted-foreground mt-0.5'>
            {total} subcategories
          </div>
        </div>
      </Link>
      {total > 0 && (
        <div className='mt-3 flex flex-wrap gap-1.5'>
          {shownSubs.map(sub => (
            <Link
              key={sub.id}
              to={nodeUrl(sub)}
              className='text-[11px] rounded-full bg-secondary hover:bg-secondary/70 px-2 py-1'
              data-testid={`categories-sub-${sub.slug}`}
            >
              {sub.name}
            </Link>
          ))}
          {hiddenCount > 0 && (
            <button
              type='button'
              onClick={() => setExpanded(e => !e)}
              className='text-[11px] rounded-full border px-2 py-1 text-primary hover:bg-primary/5 inline-flex items-center gap-1 transition-colors'
              aria-expanded={expanded}
              data-testid={`categories-expand-${main.slug}`}
            >
              {expanded
                ? <>Show less <ChevronDown className='h-3 w-3 rotate-180 transition-transform' /></>
                : <>+{hiddenCount} more <ChevronDown className='h-3 w-3 transition-transform' /></>}
            </button>
          )}
        </div>
      )}
    </Card>
  );
};

const CategoriesDirectory = () => {
  const [sellTree, setSellTree] = useState([]);
  const [wantedTree, setWantedTree] = useState([]);
  const [rentTree, setRentTree] = useState([]);
  const [serviceTree, setServiceTree] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/categories/tree', { params: { listing_type: 'sell' } }),
      api.get('/categories/tree', { params: { listing_type: 'wanted' } }),
      api.get('/categories/tree', { params: { listing_type: 'rent' } }),
      api.get('/categories/tree', { params: { listing_type: 'service' } }),
    ]).then(([s, w, r, sv]) => {
      setSellTree(s.data || []);
      setWantedTree(w.data || []);
      setRentTree(r.data || []);
      setServiceTree(sv.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const renderSection = (listingType, tree) => (
    <section key={listingType}>
      <SectionHeader listingType={listingType} count={tree.length} />
      {tree.length === 0 ? (
        <Card className='p-8 text-center text-muted-foreground'>
          No categories yet under {listingType}.
        </Card>
      ) : (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
          {tree.map(main => (
            <CategoryCard key={main.id} main={main} listingType={listingType} />
          ))}
        </div>
      )}
    </section>
  );

  return (
    <div className='indus-container py-8'>
      <SEOHead
        title='Industrial Categories: Machines, Tools & Materials India | Indus Fact'
        description='Explore all industrial categories to buy, sell, rent & service — CNC machines, tools, bearings, raw materials, electricals & more. Browse Indus Fact.'
        keywords='industrial categories, machines, tools, raw materials, bearings, electricals, B2B India'
        canonical={typeof window !== 'undefined' ? `${window.location.origin}/categories` : undefined}
      />
      <div className='mb-2'>
        <h1 className='text-3xl sm:text-4xl font-semibold tracking-tight'>All Categories</h1>
        <p className='text-muted-foreground text-sm mt-1'>
          Industrial B2B categories organised by listing type — Sell, Wanted, Rent and Services.
        </p>
      </div>
      {loading ? (
        <div className='py-16 text-center text-muted-foreground'>Loading categories…</div>
      ) : (
        <>
          {renderSection('sell', sellTree)}
          {renderSection('wanted', wantedTree)}
          {renderSection('rent', rentTree)}
          {renderSection('service', serviceTree)}
        </>
      )}
    </div>
  );
};

export default CategoriesDirectory;
